import React, { useMemo } from "react";
import { BetCard, BetMessage, type BetMessageVariant, BetPick } from "library";
import { getRoute, routeParam } from "utilities/route";
import type { IBetPickPickems } from "types/PickemsTypes";
import type { PickemsEntry } from "hooks/firestore/pickems/types";
import { PickemsTransforms } from "selectors";
import { setMenu } from "utilities/UI/uiSlice";
import {
  useDispatch,
  useHasPermission,
  useIsMobile,
  usePermission,
  useSelector,
} from "hooks";
import { useEntry } from "hooks/firestore/betting/useEntries";
import { useIsLoggedIn } from "hooks";
import {
  usePickems,
  usePickemsContest,
  usePickemsMarkets,
} from "hooks/firestore";
import { getUnauthorisedMessageElement } from "./PickSlip";
import { selectContestPicks, selectNewContestPicks } from "./pickslipSlice";
import { UnauthorizedPickemsAlerts } from "sections/Betting/Betslip/components/UnauthorizedAlerts";
import * as styles from "./PickSlip.module.scss";
import { Button } from "components/Button";
import { ReactComponent as ChevronDown } from "assets/icons/arrow-down-1.svg";
import cx from "classnames";
import { Tab, Tabs, TabsList } from "components/Tabs";
import { ScrollArea } from "components/ScrollArea";
import { PageMessage } from "components/PageMessage";
import { ReactComponent as EmptyIcon } from "sections/Pickems/assets/empty.svg";
import { Clamp } from "components/Clamp";
import { Alert } from "sections/Betting/Betslip/components/Alert";
import type { NullableMaybe } from "types/utilities";
import { isEmpty } from "lodash";
import { navigate } from "utilities/navigate";
import { ReactComponent as CheckIcon } from "components/assets/success.svg";
import PickSlipFooter from "./PickSlipFooter";

type AlertMessage = {
  type: BetMessageVariant;
  message: string;
  cta?: string;
  ctaCallback?: () => void;
  canStillSubmit?: boolean;
};

const findPickProblem = (
  pick: IBetPickPickems,
  actions: ReturnType<typeof usePickems>["actions"],
  marketNotActive: boolean,
  marketPointsChanged: boolean | undefined,
  isPointsOnlyChange: boolean,
): NullableMaybe<AlertMessage> => {
  if (!pick) {
    return null;
  }

  if (pick.errorMessage) {
    return {
      type: "warning",
      message: pick.errorMessage,
    };
  }

  if (!pick.scheduledStartTime) {
    return {
      type: "warning",
      message: "The match you picked has not been scheduled to play",
    };
  }

  if (marketNotActive) {
    return {
      type: "warning",
      message: "A Market related to this selection is not currently active",
    };
  }

  if (marketPointsChanged) {
    return {
      type: "danger",
      message: "Score Updated",
      cta: "Accept",
      ctaCallback: () => {
        actions.acceptPick(pick);
      },
    };
  }

  if (isPointsOnlyChange) {
    return {
      type: "warning",
      message: "You are updating your points for an existing pick",
      canStillSubmit: true, // other warnings cannot submit
    };
  }
};

const PickSlipContest = () => {
  const isLoggedIn = useIsLoggedIn();
  const contestId = routeParam(getRoute(), "contest");
  const { props, actions } = usePickems();
  const [contest] = usePickemsContest(contestId);
  const [entry] = useEntry<PickemsEntry>(contestId);
  const [marketsData, marketsLoading] = usePickemsMarkets(contestId);
  const picks = useSelector(selectContestPicks);
  const newPicks = useSelector((state) => selectNewContestPicks(state, entry));
  const permission = usePermission("submitFreeRoster");
  const canSubmitPickems = useHasPermission("submitPickems");
  const isMobile = useIsMobile();
  const dispatch = useDispatch();

  const markets = useMemo(() => {
    if (marketsLoading || !marketsData) return [];

    const marketsCopy = [...marketsData];
    marketsCopy?.forEach((market) => {
      Object.keys(market.outcomes ?? {}).forEach((outcomeId) => {
        const outcome = market.outcomes[outcomeId];
        const picksCopy = picks?.map((p) => ({ ...p })) ?? [];
        const pick = picksCopy.find(
          (pick: IBetPickPickems) => pick.outcomeId === outcome.id,
        );
        if (
          !props.confirmContestId &&
          pick &&
          pick.freshOdds !== outcome.odds
        ) {
          // if the points change back to the original points before accepting we need to not show the odds changed message
          pick.oddsChanged = outcome.odds !== pick.lockedOdds;
          pick.freshOdds = outcome.odds;
          actions.updatePickslip({ picks: picksCopy });
        }
        outcome.selected = !!pick;
        outcome.selectedAgainst =
          !pick && !!picksCopy.find((x) => x.marketId === market.id);
      });
    });

    return marketsCopy;
  }, [marketsData]);

  const unauthorisedMessage = getUnauthorisedMessageElement(
    isLoggedIn,
    permission,
  );

  const pickCount = picks?.length ?? 0;
  const newPickCount =
    newPicks?.filter((pick) => {
      const market = marketsData.find(
        (market) => market.event?.id === pick.eventId,
      );

      return market?.status === "ACTIVE";
    }).length ?? 0;

  const bodyChildren: JSX.Element[] = [];

  const existingSelections = entry?.picks ?? [];
  const filteredExistingSelections = existingSelections?.filter((selection) => {
    const market = marketsData.find(
      (market) => market.event?.id === selection.eventId,
    );

    if (market?.status !== "ACTIVE") return false;

    return selection?.result === "UNDECIDED";
  });
  const existingPickCount = filteredExistingSelections?.length || 0;

  const marketsWithOutcomes =
    markets.filter((m) => Object.keys(m.outcomes ?? {})?.length) ?? [];
  const marketCount = marketsWithOutcomes.length;
  const activeMarketsCount = markets.reduce((sum, market) => {
    const hasOutcomes = !!Object.keys(market.outcomes ?? {}).length;
    if (
      PickemsTransforms.isPickAvailable(market, market.event) &&
      hasOutcomes
    ) {
      return sum + 1;
    } else {
      return sum;
    }
  }, 0);

  const remainingCount = Math.max(
    activeMarketsCount - newPickCount - existingPickCount,
    0,
  );

  let canMakePick =
    canSubmitPickems &&
    pickCount > 0 &&
    remainingCount < 1 &&
    !unauthorisedMessage;

  const picksAccepted = !!props.pickAcceptedId;

  const stickyAlerts: JSX.Element[] = [];
  const globalAlerts: JSX.Element[] = [];

  if (picksAccepted) {
    stickyAlerts.push(
      <Alert
        key={`picks-accepted`}
        variant={`info`}
        icon={<CheckIcon />}
        clamp={
          <Clamp
            className={styles.clickable}
            onClick={() =>
              navigate(
                `/entries/${contest.status === "LIVE" ? "live" : "upcoming"}/`,
              )
            }
            variant={`info`}
          >
            View
          </Clamp>
        }
        className={styles.picksAccepted}
      >
        Picks Accepted: P-{contest?.contestNumber ?? null}-
        {props.pickAcceptedId}
      </Alert>,
    );
  }

  if (props.error) {
    globalAlerts.push(
      <Alert
        key={`picks-error`}
        variant={`danger`}
        clamp={
          <Clamp
            className={styles.clickable}
            onClick={() => actions.updatePickslip({ error: null })}
            variant={`danger`}
          >
            Got It
          </Clamp>
        }
      >
        {props.error}
      </Alert>,
    );
    canMakePick = false;
  }

  if (unauthorisedMessage) {
    globalAlerts.push(unauthorisedMessage);
  }
  if (
    (pickCount > 0 && remainingCount > 0) ||
    (existingPickCount > 0 && remainingCount > 0)
  ) {
    globalAlerts.push(
      <Alert key={`picks-remaining`} variant={`warning`}>
        {remainingCount +
          (remainingCount === 1 ? " pick" : " picks") +
          " remaining"}
      </Alert>,
    );
  }

  if (pickCount > 0 || existingPickCount > 0) {
    bodyChildren.push(
      ...picks.map((pick: IBetPickPickems) => {
        const selection = entry?.picks?.find((s) => s.eventId === pick.eventId);
        const market = contest?.markets?.find((x) => x.id === pick.marketId);
        const entryPoints = selection ? selection.odds : pick.odds;
        const isAdjustment = pick.odds !== entryPoints;
        const isPointsOnlyChange =
          isAdjustment && selection?.outcomeId === pick.outcomeId;
        const marketNotActive = market?.status !== "ACTIVE";

        const problem: NullableMaybe<AlertMessage> = findPickProblem(
          pick,
          actions,
          marketNotActive,
          pick.oddsChanged,
          isPointsOnlyChange,
        );

        if (
          problem?.type === "danger" ||
          (problem?.type === "warning" && !problem?.canStillSubmit)
        ) {
          canMakePick = false;
        }

        return (
          <div
            key={pick.sourceId + ":" + pick.outcomeId}
            className={styles.cardWrapper}
          >
            <BetCard
              message={
                !canSubmitPickems ? (
                  <BetMessage
                    type={"warning"}
                    message={"You are not permitted to perform this action"}
                  />
                ) : problem ? (
                  <BetMessage
                    type={problem.type || "danger"}
                    message={problem.message}
                    actionText={problem.cta}
                    action={problem.ctaCallback}
                  />
                ) : null
              }
            >
              <BetPick
                selection={{
                  ...pick,
                  marketName: pick.odds ? "POINTS" : "",
                }}
                oddsFormat="points"
                doRemoveSelection={() => {
                  actions.removePick(pick);
                }}
                contest={pick.contest}
                eventId={pick.eventId}
              />
            </BetCard>
          </div>
        );
      }),
    );
  }

  const contentBody = (
    <>
      <UnauthorizedPickemsAlerts />
      {globalAlerts}
      {bodyChildren}
      {pickCount < 1 && (
        <PageMessage
          className={styles.empty}
          title={"Your Pickslip is Empty"}
          subTitle={
            existingPickCount < 1
              ? "Select and submit your picks to enter the contest"
              : "Swap and submit your picks to update your contest entry"
          }
          image={<EmptyIcon />}
        >
          {isMobile && permission === "GRANTED" && (
            <Button size={"md"} onClick={() => dispatch(setMenu("none"))}>
              View Contest
            </Button>
          )}
        </PageMessage>
      )}
    </>
  );

  return (
    <div className={styles.container}>
      <Tabs
        value="pickems"
        className={cx(styles.tabs, {
          [styles.largeFooter]: pickCount > 0,
        })}
      >
        <TabsList
          containerClassName={styles.tabListContainer}
          widget={
            <div
              onClick={() => dispatch(setMenu("none"))}
              className={styles.close}
            >
              <ChevronDown />
            </div>
          }
          className={styles.tabList}
        >
          <Tab badge={pickCount.toString() || "0"} value="pickems">
            Pick'ems
          </Tab>
        </TabsList>
        <ScrollArea hideHorizontalScrollbar className={styles.scrollArea}>
          {!isEmpty(stickyAlerts) && (
            <div className={styles.stickyAlerts}>{stickyAlerts}</div>
          )}
          <div className={styles.content}>{contentBody}</div>
        </ScrollArea>
      </Tabs>
      {pickCount > 0 && (
        <PickSlipFooter
          contestId={contestId}
          existingPickCount={existingPickCount}
          canMakePick={canMakePick}
          pickCount={pickCount}
          marketCount={marketCount}
        />
      )}
    </div>
  );
};

export default PickSlipContest;
